import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link } from "react-router-dom";
import { ArrowRight, X, Lock, MessageSquarePlus, Loader2 } from "lucide-react";
import { useWallets } from "@/hooks/use-wallets";
import { useValuationSummary } from "@/features/valuation/use-valuation-summary";
import { planAiResponse } from "@/lib/ai-search/match";
import { TOPICS } from "@/lib/ai-search/topics";
import { ResponseBlockView } from "@/lib/ai-search/blocks";
import type { AccentTone, AiResponse } from "@/lib/ai-search/types";
import { cn } from "@/lib/utils";

const ACCENT_BADGE: Record<AccentTone, string> = {
  info: "bg-info-soft text-info",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  danger: "bg-danger-soft text-danger",
  pending: "bg-pending-soft text-pending",
};

function AiSparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("text-primary", className)}
      aria-hidden="true"
    >
      <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
      <path d="M20 2v4" />
      <path d="M22 4h-4" />
      <circle cx="4" cy="20" r="2" />
    </svg>
  );
}

function SearchInput({
  query,
  onChange,
  onSubmit,
  placeholder,
  autoFocus,
  inputRef,
  disabled,
}: {
  query: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  autoFocus?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="relative"
    >
      <div className="flex h-14 items-center rounded-full border border-line bg-white pl-4 pr-[52px] shadow-[0_4px_24px_rgba(36,105,255,0.08)] transition-shadow focus-within:border-primary focus-within:shadow-[0_4px_24px_rgba(36,105,255,0.14)]">
        <AiSparkleIcon className="pointer-events-none size-5 shrink-0" />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label="Ask Alloy AI"
          className="h-full w-full border-0 bg-transparent pl-3 text-base text-ink outline-none placeholder:text-muted disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={!query.trim() || disabled}
          aria-label="Search"
          className="absolute right-2 top-1/2 size-10 -translate-y-1/2 rounded-full bg-primary text-on-primary transition-opacity disabled:opacity-30"
        >
          <ArrowRight aria-hidden className="mx-auto size-5" />
        </button>
      </div>
    </form>
  );
}

/**
 * Scoped, real-data port of DesignDev's AI Search overlay — see
 * `lib/ai-search/topics.ts` for why only 2 of DesignDev's 8 topics are
 * wired here. The heuristic keyword-match architecture is unchanged
 * (`lib/ai-search/match.ts`), pointed at the live wallet list
 * (`useWallets`) and never at seeded or mock wallets.
 */
export function AiSearchOverlay({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [query, setQuery] = useState("");
  const [turns, setTurns] = useState<{ query: string; response: AiResponse | null }[]>([]);
  const [pendingQuery, setPendingQuery] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomInputRef = useRef<HTMLInputElement>(null);
  // Real wallets from the gateway, loaded only once the overlay is opened. A
  // failed or empty read answers from an empty list ("0 total"), never from
  // seeded wallets.
  const { wallets, loading: walletsLoading, error: walletsError } = useWallets({ enabled: open });
  const { summary: valuation, loading: valuationLoading } = useValuationSummary(open && !walletsLoading && !walletsError);
  const dataLoading = open && (walletsLoading || valuationLoading);
  const [thinkingDone, setThinkingDone] = useState(false);

  const LOADING_MS = 700;

  function submit(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;
    setPendingQuery(trimmed);
    setThinkingDone(false);
    setQuery("");
    window.setTimeout(() => setThinkingDone(true), LOADING_MS);
  }

  // Answer only once the live wallet data has finished loading, so a question
  // asked while the list is still in flight is never answered from nothing.
  useEffect(() => {
    if (!pendingQuery || !thinkingDone || dataLoading) return;
    const planned = planAiResponse(pendingQuery, { wallets, valuation });
    // A failed wallet read is not an empty portfolio: say so instead of
    // answering "0 wallets" from the empty fallback list.
    const response: AiResponse | null =
      planned && walletsError
        ? {
            summary: "I couldn't read your wallets just now, so I can't answer this yet. Try again in a moment.",
            blocks: [],
            actions: [{ label: "View wallets", href: "/wallets" }],
          }
        : planned;
    setTurns((prev) => [...prev, { query: pendingQuery, response }]);
    setPendingQuery(null);
    setThinkingDone(false);
  }, [pendingQuery, thinkingDone, dataLoading, wallets, valuation, walletsError]);

  function reset(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setQuery("");
      setTurns([]);
      setPendingQuery(null);
    }
  }

  const hasTurns = turns.length > 0 || !!pendingQuery;

  useEffect(() => {
    // `hasTurns` turns true the moment a question is submitted, which is also
    // the moment the follow-up input is `disabled` — focus() on a disabled
    // input is a no-op, and the effect would never re-run once the answer
    // landed, so the hand-off has to wait for the pending question to clear.
    if (hasTurns && !pendingQuery) {
      bottomInputRef.current?.focus();
    }
  }, [hasTurns, pendingQuery]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns.length, pendingQuery]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={reset}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="ai-search-backdrop fixed inset-0 z-50 bg-[var(--ads-ai-backdrop)] backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="ai-search-panel fixed inset-x-4 top-[8vh] z-50 mx-auto flex max-h-[min(84vh,820px)] min-h-[420px] max-w-[720px] flex-col overflow-hidden outline-none"
        >
          <DialogPrimitive.Title className="sr-only">Alloy AI</DialogPrimitive.Title>
          <div className="relative flex flex-1 flex-col min-h-0 overflow-hidden">
            <div aria-hidden className="ai-search-glow pointer-events-none absolute -inset-10 -z-10 rounded-[40px] blur-lg" />
            <div className="flex flex-1 flex-col min-h-0 overflow-hidden rounded-[24px] border border-white/60 bg-gradient-to-br from-[var(--ads-ai-panel-from)] via-[var(--ads-ai-panel-via)] to-[var(--ads-ai-panel-to)] shadow-[0_24px_80px_rgba(15,23,42,0.16)]">
              <div className="flex shrink-0 items-center justify-between px-6 pt-6">
                <div className="flex items-center gap-2">
                  <AiSparkleIcon className="size-5" />
                  <span className="text-base font-bold text-ink">Alloy AI</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <DialogPrimitive.Close
                    aria-label="Close"
                    className="grid size-9 place-items-center rounded-full border border-line bg-white/80 text-muted shadow-sm outline-none transition-colors hover:bg-white hover:text-ink focus-visible:ring-2 focus-visible:ring-primary-soft"
                  >
                    <X aria-hidden className="size-4" />
                  </DialogPrimitive.Close>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">ESC</span>
                </div>
              </div>

              <div
                className={cn(
                  "shrink-0 overflow-hidden px-6 transition-all duration-300 ease-out",
                  hasTurns ? "max-h-0 opacity-0" : "max-h-[260px] pt-2 opacity-100"
                )}
              >
                <h2 className="text-center text-[32px] font-bold leading-tight tracking-tight text-ink">
                  How can I help you{" "}
                  <span className="bg-gradient-to-r from-[var(--ads-primary-500)] to-[var(--ads-pending)] bg-clip-text text-transparent">today</span>?
                </h2>
                <p className="mx-auto mt-1 max-w-[440px] text-center text-sm text-muted">
                  Get instant answers and insights from your organization&apos;s data.
                </p>
                <div className="mt-4">
                  <SearchInput
                    query={query}
                    onChange={setQuery}
                    onSubmit={() => submit(query)}
                    placeholder="Ask Alloy about your data..."
                    autoFocus
                    disabled={!!pendingQuery}
                  />
                </div>
              </div>

              <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-y-auto px-6 py-3">
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-6 top-0 z-10 h-8 bg-gradient-to-b from-[var(--ads-ai-panel-from)] to-transparent"
                />
                {!hasTurns ? (
                  <div className="text-center">
                    <div className="my-2 flex items-center gap-3 text-xs font-medium text-muted">
                      <span className="h-px flex-1 bg-line/70" />
                      Try asking about
                      <span className="h-px flex-1 bg-line/70" />
                    </div>

                    <div className="grid grid-cols-1 gap-3 text-left sm:grid-cols-2">
                      {TOPICS.map((topic) => (
                        <button
                          key={topic.id}
                          type="button"
                          onClick={() => submit(topic.suggestedQuestion)}
                          className="group flex flex-col items-start gap-3 rounded-2xl border border-line bg-white/60 p-4 text-left transition-colors hover:border-primary hover:bg-white"
                        >
                          <span className={cn("grid size-10 shrink-0 place-items-center rounded-xl", ACCENT_BADGE[topic.accent])}>
                            <topic.icon aria-hidden className="size-5" />
                          </span>
                          <span className="text-sm font-semibold text-ink">{topic.cardTitle}</span>
                          <span className="text-left text-xs leading-relaxed text-muted">{topic.cardDescription}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-5">
                    {turns.map((turn, idx) => (
                      <div key={idx} className="flex flex-col gap-2">
                        <div className="self-end rounded-2xl rounded-br-md bg-primary px-4 py-2 text-sm font-medium text-on-primary">
                          {turn.query}
                        </div>
                        <div className="text-left">
                          {turn.response ? (
                            <>
                              <p className="text-sm leading-relaxed text-text">{turn.response.summary}</p>
                              {turn.response.blocks.length > 0 && (
                                <div className="mt-2 flex flex-col gap-2">
                                  {turn.response.blocks.map((block, i) => (
                                    <ResponseBlockView key={i} block={block} />
                                  ))}
                                </div>
                              )}
                              {turn.response.actions.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-4">
                                  {turn.response.actions.map((action) => (
                                    <Link
                                      key={action.href}
                                      to={action.href}
                                      onClick={() => reset(false)}
                                      className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                                    >
                                      {action.label}
                                      <ArrowRight aria-hidden className="size-3.5" />
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="rounded-lg border border-line bg-bg p-4 text-sm text-muted">
                              I can only answer questions about your Alloy data — try asking about AUM or wallets.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {pendingQuery && (
                      <div className="flex flex-col gap-2">
                        <div className="self-end rounded-2xl rounded-br-md bg-primary px-4 py-2 text-sm font-medium text-on-primary">
                          {pendingQuery}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted">
                          <Loader2 aria-hidden className="size-4 animate-spin text-primary" />
                          Alloy AI is thinking…
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                className={cn(
                  "shrink-0 overflow-hidden px-6 transition-all duration-300 ease-out",
                  hasTurns ? "max-h-[160px] opacity-100" : "max-h-0 opacity-0"
                )}
              >
                <SearchInput
                  query={query}
                  onChange={setQuery}
                  onSubmit={() => submit(query)}
                  placeholder="Ask a follow-up or new question..."
                  inputRef={bottomInputRef}
                  disabled={!!pendingQuery}
                />
                <div className="mt-2 flex items-center justify-end pb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setTurns([]);
                      setQuery("");
                      setPendingQuery(null);
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-muted hover:text-ink"
                  >
                    <MessageSquarePlus aria-hidden className="size-3.5" />
                    New Question
                  </button>
                </div>
              </div>

              <div className="flex shrink-0 items-center justify-center gap-1.5 border-t border-line/40 px-6 py-3 text-xs text-muted">
                <Lock aria-hidden className="size-3" />
                Alloy AI uses only your organization&apos;s data. No external or public data is accessed.
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
